// The `/design` route (spec §2 "Design record"): a single scrollable page that renders the three
// documents in `docs/design/` — the planner's workflow map, the AI (artificial intelligence) UX pattern
// decisions, and the dated trade-off log — plus a short "About this project" intro and a fourth, live
// section: the AI audit, every model call this tab has made (src/ui/design/AiAudit.tsx).
//
// The markdown carries figures: a hand-authored workflow diagram, annotated screenshots per AI pattern,
// a before/after of the interaction change. They live in `public/design/` and are referenced with
// root-absolute paths (`/design/…`) so the same markdown renders here and on the deployed site.
//
// The page exists so a reviewer can read the design reasoning behind WINDTUNNEL from inside the running
// app, not only from files in the repository.
//
// No dropdowns (spec §8): the left index is a plain list of chips, and the only interaction is "click to
// scroll" and a "Back" chip that returns to wherever the player was — passed in as `returnTo`/`onBack` so
// this component stays ignorant of whether it was opened from a stage or from the `?design` URL flag
// (see App.tsx).

import { useRef } from 'react';
import { marked } from 'marked';
import type { Stage } from '../../state/store';
import { Chip } from '../cards/ui';
import { Wordmark } from '../Wordmark';
import { AiAudit } from './AiAudit';

import workflowMd from '../../../docs/design/workflow-map.md?raw';
import aiPatternsMd from '../../../docs/design/ai-patterns.md?raw';
import tradeoffsMd from '../../../docs/design/tradeoffs.md?raw';

const STAGE_LABEL: Record<Stage, string> = {
  scenario: 'the scenario',
  planning: 'planning',
  live: 'the fight',
  debrief: 'the debrief',
  design: 'the app',
};

interface Section {
  id: string;
  index: string;
  title: string;
  md: string;
}

const SECTIONS: Section[] = [
  { id: 'workflow', index: 'Workflow map', title: 'The planning loop, and where WINDTUNNEL sits', md: workflowMd },
  { id: 'ai-patterns', index: 'AI patterns', title: 'AI UX patterns, and when they do not fit', md: aiPatternsMd },
  { id: 'tradeoffs', index: 'Trade-off log', title: 'Trade-off log', md: tradeoffsMd },
];

/** Marked renders synchronously with no async extensions loaded; every table gets an overflow wrapper
 *  so a wide table (the trade-off log runs seven columns) scrolls instead of breaking the 1280 px layout. */
function renderMarkdown(raw: string): string {
  const html = marked.parse(raw, { async: false, gfm: true }) as string;
  return html.replace(/<table>/g, '<div class="prose-wt-table-wrap"><table>').replace(/<\/table>/g, '</table></div>');
}

export function DesignRecord({ returnTo, onBack }: { returnTo: Stage; onBack: () => void }) {
  const refs = useRef<Record<string, HTMLElement | null>>({});

  const scrollTo = (id: string) => {
    refs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="design-record" className="thin-scroll h-full overflow-y-auto bg-ink">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-700 bg-slate-900/95 px-6 py-3 backdrop-blur-sm">
        <Chip onClick={onBack} testId="design-back" title={`Return to ${STAGE_LABEL[returnTo]}`}>
          ← back
        </Chip>
        <Wordmark />
        <span className="mono text-[13px] tracking-[0.1em] text-paper">Design record</span>
      </header>

      <div className="mx-auto flex max-w-[1240px] items-start gap-8 px-6 py-8">
        <nav className="sticky top-[57px] hidden w-[200px] shrink-0 space-y-1 md:block">
          <div className="mono mb-2 text-[10px] uppercase tracking-[0.2em] text-fog">Index</div>
          <IndexEntry id="about" label="About this project" onClick={scrollTo} />
          {SECTIONS.map((s) => (
            <IndexEntry key={s.id} id={s.id} label={s.index} onClick={scrollTo} />
          ))}
          <IndexEntry id="audit" label="AI audit" onClick={scrollTo} />
        </nav>

        <div className="min-w-0 flex-1 space-y-10">
          <section
            data-testid="design-section-about"
            ref={(el) => { refs.current.about = el; }}
            className="rounded-sm border border-slate-700 bg-slate-900/70 p-5"
          >
            <h1 className="mono text-[12px] uppercase tracking-[0.25em] text-paper">About this project</h1>
            <figure className="prose-wt mt-3">
              <img
                src="/design/thesis-cop-vs-truth.png"
                alt="The debrief's two replay panes side by side: on the left, what the common operating picture showed — two enemy diamonds; on the right, ground truth for the same minute — eighteen."
              />
              <figcaption>
                The thesis, in one frame: the same minute of the same fight, as the common operating
                picture (COP) showed it and as it actually was. The product exists to put those two
                pictures next to each other, because the gap between them is what a planner never gets
                to see.
              </figcaption>
            </figure>
            <p className="mt-3 max-w-[74ch] text-[15px] leading-relaxed text-paper">
              WINDTUNNEL is a browser-based wargame for military planners: a battalion S-3 (the operations
              officer) plans a course of action (COA) on real terrain, fights it in real time against a
              scripted red cell, and gets a debrief comparing what the common operating picture (COP)
              showed against what actually happened. It was built end to end — terrain pipeline,
              deterministic simulation, three large language model (LLM) advisors, and this design record
              — as a compressed three-day interview artifact for Onebrief&apos;s Senior AI (artificial
              intelligence) Product Designer role, to show how AI can sit inside a real planning workflow
              as staff rather than as an adjudicator. It is deployed at{' '}
              <a
                href="https://windtunnel.pages.dev"
                target="_blank"
                rel="noreferrer"
                className="text-[var(--color-friendly)] underline decoration-dotted underline-offset-4"
              >
                https://windtunnel.pages.dev
              </a>
              ; repo: (private).
            </p>
          </section>

          {SECTIONS.map((s) => (
            <section
              key={s.id}
              data-testid={`design-section-${s.id}`}
              ref={(el) => { refs.current[s.id] = el; }}
              className="rounded-sm border border-slate-700 bg-slate-900/70 p-5"
            >
              <div
                className="prose-wt"
                // The three files are project-owned markdown checked into the repo, not user input.
                dangerouslySetInnerHTML={{ __html: renderMarkdown(s.md) }}
              />
            </section>
          ))}

          {/* Fourth section: not a markdown file but a live reading of this tab's own model calls. */}
          <section
            data-testid="design-section-audit"
            ref={(el) => { refs.current.audit = el; }}
            className="rounded-sm border border-slate-700 bg-slate-900/70 p-5"
          >
            <AiAudit />
          </section>
        </div>
      </div>
    </div>
  );
}

function IndexEntry({ id, label, onClick }: { id: string; label: string; onClick: (id: string) => void }) {
  return (
    <button
      type="button"
      data-testid={`design-index-${id}`}
      onClick={() => onClick(id)}
      className="mono block w-full rounded-sm border border-transparent px-2 py-1.5 text-left text-[11px] tracking-[0.06em] text-fog hover:border-slate-700 hover:text-paper"
    >
      {label}
    </button>
  );
}
